import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Hash, Cpu, Calendar, Factory, Gauge, FileText, AlertTriangle, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";

/**
 * SYS-001 · Customer web "My System" — read-only.
 *
 * Consumes the canonical envelope from
 * ``GET /api/projects/{id}/equipment`` (customer-safe serializer).
 * The same endpoint powers the mobile screen and the Operations
 * workspace view — no parallel schema, no duplicated data.
 *
 * Fields without values render a clear "Not recorded" state — never
 * invented from product names, docs or unrelated project fields.
 */
const NOT_RECORDED = "Not recorded";

function humanDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return iso; }
}

function formatCapacity(cap) {
    if (!cap) return null;
    if (cap.value == null && !cap.unit) return null;
    if (cap.value == null) return cap.unit;
    if (!cap.unit) return String(cap.value);
    return `${cap.value} ${cap.unit}`;
}

async function openSecureDocument(docId) {
    try {
        const { token } = await api.documentDownloadToken(docId);
        window.open(
            `${api.documentDownloadUrl(docId)}?dt=${encodeURIComponent(token)}`,
            "_blank", "noopener,noreferrer",
        );
    } catch {
        toast.error("Could not open that document. Please try again.");
    }
}

export default function MySystem() {
    const { user } = useAuth();
    const [projectId, setProjectId] = useState(null);
    const [systemSummary, setSystemSummary] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const p = user?.role === "installer"
                ? (await api.listProjects())[0]
                : await api.myProject();
            if (!p) { setLoading(false); return; }
            setProjectId(p.id);
            const resp = await api.listEquipment(p.id);
            // The envelope shape is `{ system, equipment, role }`. Legacy
            // callers used to receive a raw array — guard against both so
            // an in-flight rollout never blanks the customer view.
            if (Array.isArray(resp)) {
                setItems(resp);
                setSystemSummary(null);
            } else {
                setItems(resp?.equipment || []);
                setSystemSummary(resp?.system || null);
            }
        } catch (e) {
            setError(e?.response?.data?.detail || "We couldn't load your system just now.");
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { load(); }, [load]);

    const backTo = user?.role === "installer" ? "/installer" : "/dashboard";

    return (
        <div className="min-h-screen bg-[#F5F5F7] pb-20" data-testid="system-page">
            <PageContainer width="medium">
                <PageHeader
                    eyebrow="CUSTOMER"
                    title="My System"
                    subtitle="Every piece of hardware Pearl Energy installed for you."
                    icon={Cpu}
                    backFallback={backTo}
                    testid="system"
                />

                {loading && (
                    <div className="mt-6 pe-card p-8 flex items-center justify-center text-sm text-[#4B5565]" data-testid="system-loading">
                        <Loader2 className="animate-spin mr-2" size={16} /> Loading your system…
                    </div>
                )}

                {!loading && error && (
                    <div className="mt-6 pe-card p-8 text-center" data-testid="system-error">
                        <AlertTriangle size={22} className="mx-auto text-[#B91C1C]" />
                        <div className="mt-3 text-sm text-[#131B2F]">{error}</div>
                        <button
                            onClick={load}
                            className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                            data-testid="system-retry"
                        >
                            Try again
                        </button>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        {systemSummary && systemSummary.total_items > 0 && (
                            <div className="mt-6 pe-card p-5" data-testid="system-summary">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <SummaryStat label="Installed items" value={String(systemSummary.total_items)} />
                                    <SummaryStat label="Installed since" value={humanDate(systemSummary.earliest_installation_date) || NOT_RECORDED} />
                                    <SummaryStat label="Commissioned" value={humanDate(systemSummary.latest_commissioning_date) || NOT_RECORDED} />
                                </div>
                                {systemSummary.has_incomplete_data && (
                                    <div className="mt-3 text-xs text-[#78350F] bg-[#FFFBEB] border border-[#FDE68A] rounded-md p-2 flex items-start gap-2">
                                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                        Some details are still being recorded by our team. Missing values appear as “{NOT_RECORDED}”.
                                    </div>
                                )}
                            </div>
                        )}

                        {items.length === 0 ? (
                            <div className="mt-6 pe-card p-8 text-center text-sm text-[#4B5565]" data-testid="system-empty">
                                No equipment has been recorded for your project yet. Once your survey and installation are logged, it'll appear here.
                            </div>
                        ) : (
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="system-grid">
                                {items.map((eq) => (
                                    <EquipmentCard key={eq.id} eq={eq} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </PageContainer>
        </div>
    );
}

function SummaryStat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wide text-[#4B5565]">{label}</div>
            <div className="text-[#131B2F] font-medium mt-0.5">{value}</div>
        </div>
    );
}

function EquipmentCard({ eq }) {
    const type = eq.equipment_type || "Equipment";
    const display = eq.display_name || eq.model || NOT_RECORDED;
    const capacity = formatCapacity(eq.rated_capacity);
    const installed = humanDate(eq.installation_date);
    const commissioned = humanDate(eq.commissioning_date);
    const warrantyExpiry = humanDate(eq.warranty_expiry);

    return (
        <div className="pe-card pe-card-hover overflow-hidden" data-testid={`equip-${type.toLowerCase().replace(/\s+/g,"-")}`}>
            {eq.image_url && (
                <div className="relative aspect-[16/10] bg-[#E8E8ED]">
                    <img
                        src={eq.image_url}
                        alt={type}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                    />
                    <div className="absolute top-3 left-3 pe-chip !bg-white/95 backdrop-blur">
                        {type}
                    </div>
                </div>
            )}
            <div className="p-5 md:p-6">
                {!eq.image_url && (
                    <div className="pe-chip mb-2 !bg-[#F5F5F7]">{type}</div>
                )}
                <div className="text-lg text-[#131B2F] font-medium" data-testid="equip-display">{display}</div>
                {eq.manufacturer && (
                    <div className="text-sm text-[#4B5565] mt-0.5 flex items-center gap-1"><Factory size={12} /> {eq.manufacturer}</div>
                )}
                {eq.quantity && (
                    <div className="text-sm text-[#4B5565] mt-0.5">Quantity: {eq.quantity}</div>
                )}
                {eq.specs && (
                    <p className="text-sm text-[#131B2F] mt-3 leading-relaxed">{eq.specs}</p>
                )}
                <div className="mt-4 pt-4 border-t border-[#E5E7EB] space-y-2">
                    <DetailRow icon={Hash} label="Serial" value={eq.serial_number} />
                    <DetailRow icon={Gauge} label="Rated capacity" value={capacity} />
                    <DetailRow icon={Calendar} label="Installed" value={installed} />
                    <DetailRow icon={Calendar} label="Commissioned" value={commissioned} />
                    <DetailRow icon={ShieldCheck} label="Warranty" value={eq.warranty} />
                    <DetailRow icon={ShieldCheck} label="Warranty expiry" value={warrantyExpiry} />
                    {eq.operational_status && (
                        <DetailRow icon={Cpu} label="Status" value={eq.operational_status} />
                    )}
                    {eq.customer_notes && (
                        <div className="text-sm">
                            <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565]">Notes</div>
                            <div className="text-[#131B2F] mt-0.5 leading-relaxed">{eq.customer_notes}</div>
                        </div>
                    )}
                    {eq.documents && eq.documents.length > 0 && (
                        <div className="text-sm">
                            <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565]">Documents</div>
                            <ul className="mt-1 space-y-1">
                                {eq.documents.map((d) => (
                                    <li key={d.id}>
                                        <button
                                            type="button"
                                            className="text-[#3E9E2E] hover:underline inline-flex items-center gap-1 text-sm"
                                            onClick={() => openSecureDocument(d.id)}
                                            data-testid={`equip-doc-${d.id}`}
                                        >
                                            <FileText size={12} /> {d.title || "Document"} <ExternalLink size={10} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DetailRow({ icon: Icon, label, value }) {
    const shown = value || NOT_RECORDED;
    const muted = !value;
    return (
        <div className="flex items-start gap-2 text-sm">
            <Icon size={13} strokeWidth={1.75} className="mt-0.5 text-[#4B5565] shrink-0" />
            <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565]">{label}</div>
                <div className={"mt-0.5 break-all " + (muted ? "text-[#9CA3AF] italic" : "text-[#131B2F]")}>{shown}</div>
            </div>
        </div>
    );
}
